import {Permissions} from 'auth/PermissionConstants';
import {makeReactContext} from 'lib/makeReactContext';
import {GenericResponseJSON} from 'notifications';
import {OngoingTaskResponse} from 'util/TaskRunner';

import {ReviewableQuery_reviewable as Reviewable} from './__generated/ReviewableQuery';
import {Review, ReviewAction} from './ReviewConstants';

/**
 * Independent review-error slots. Each field gates a different piece of UI, so they are populated and
 * consumed independently.
 */
export interface ReviewError {
  /**
   * Self-review review-criteria error. When present, only the self-review options are disabled; the
   * entry must clear its review criteria before it can be self-reviewed.
   */
  selfReviewCriteriaError?: JSX.Element | null;
  /**
   * Send-for-review / review-action validation error (e.g. unsaved changes, unsubmitted tables). When
   * it returns a non-null message, the primary review action is disabled. The optional action lets
   * callers apply action-specific rules (e.g. allowing RETRACT with unsubmitted tables).
   */
  sendForReviewValidationError?: (reviewableId: string, action?: ReviewAction) => string | null;
  /**
   * Custom error renderer forwarded to review modals to surface server-side errors.
   */
  renderReviewError?: (error: {responseJSON?: GenericResponseJSON}) => JSX.Element | null;
}

export interface ReviewBadgeContextValue {
  isDropdownOpen: boolean;
  setIsDropdownOpen: (isOpen: boolean) => void;
  reviewError?: ReviewError;
  onReviewChange?: (
    reviewableId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void;
  performanceMetadata?: {[key: string]: string}; // takes in performance metric
  reviewable?: Reviewable;
  reviewableId?: string;
  /** Custom rendering of additional info related to the review stage in modal */
  renderAdditionalReviewInfo?: (reviewableId: string, reviewAction: ReviewAction) => JSX.Element | null;
  openHistory?: () => void;
  reviewId?: string;
  unsubmittedTableNames?: Array<string>;
}

export function getReviewBadgeContextFallback(): ReviewBadgeContextValue {
  return {
    isDropdownOpen: false,
    setIsDropdownOpen: (isOpen) => {},
  };
}

// Controls the open/close state of the review badge
export const ReviewBadgeContext = makeReactContext<ReviewBadgeContextValue>(
  getReviewBadgeContextFallback,
  'ReviewBadgeContext'
);
